<template>
  <NavigationWrapper :collapsed="isCollapseFeatureEnabled && areControlBarsCollapsed">
    <template #top>
      <template v-if="isEditMode">
        <CButton
          v-if="showHomeButton"
          v-tooltip="t('canvasUI.canvasBuilder.components.home')"
          class="control-bar__button h-11 w-11 mt-3"
          secondary
          strong
          @click="navigateToHome()"
        >
          <template #icon>
            <CIcon icon="home" size="18" />
          </template>
        </CButton>
        <!-- Edit Wizard button moved under home button -->
        <CButton
          v-if="showWizardButton"
          v-tooltip="t('canvasUI.canvasBuilder.components.editWizard')"
          class="control-bar__button h-11 w-11"
          secondary
          strong
          @click="onWizardClick"
        >
          <template #icon>
            <CIcon icon="list-check" size="18" />
          </template>
        </CButton>
        <CButton
          v-tooltip="t('canvasUI.canvasBuilder.components.settings', { canvasName: activeCanvas?.name })"
          class="control-bar__button h-11 w-11"
          :class="{ 'mt-3': !showHomeButton }"
          :disabled="isViewOnlyMode"
          secondary
          strong
          @click="onSettingsClick"
        >
          <template #icon>
            <CIcon icon="cog" size="18" />
          </template>
        </CButton>

        <template v-if="shouldShowLockButton">
          <CButton
            v-if="canvasLock"
            v-tooltip="canvasLockTooltip"
            class="control-bar__button h-11 w-11"
            :disabled="!isCurrentUserLocker && !isOrgAdmin"
            :loading="isChangingLock"
            strong
            type="error"
            @click="onUnlockClick"
          >
            <template #icon>
              <CIcon color="success" icon="unlock" size="18" />
            </template>
          </CButton>
          <CButton
            v-else
            v-tooltip="t('canvasUI.canvasBuilder.components.lock')"
            class="control-bar__button h-11 w-11"
            :loading="isChangingLock"
            secondary
            strong
            @click="onLockClick"
          >
            <template #icon>
              <CIcon icon="lock" size="18" />
            </template>
          </CButton>
        </template>

        <CButton
          v-if="showSharingButton"
          v-tooltip="t('canvasUI.canvasBuilder.components.share')"
          class="control-bar__button h-11 w-11"
          :disabled="downloadInProgress"
          :loading="sharingInProgress"
          secondary
          strong
          @click="onShareClick"
        >
          <template #icon>
            <CIcon icon="link" size="18" />
          </template>
        </CButton>
        <CButton
          v-if="showDownloadButton && !launchDarkly.enable_scale_content"
          v-tooltip="
            online
              ? t('canvasUI.canvasBuilder.components.downloadPage')
              : t('canvasUI.canvasBuilder.components.downloadPageWhenOffline')
          "
          class="control-bar__button h-11 w-11"
          :disabled="!online || sharingInProgress"
          :loading="downloadInProgress"
          secondary
          strong
          @click="onDownloadCanvasClick"
        >
          <template #icon>
            <CIcon :icon="online ? 'download' : 'wifi-slash'" size="18" />
          </template>
        </CButton>
      </template>
      <template v-else>
        <CButton
          v-if="showHomeButton"
          v-tooltip="t('canvasUI.canvasBuilder.components.home')"
          class="control-bar__button h-11 w-11 mt-3"
          secondary
          strong
          @click="navigateToHome()"
        >
          <template #icon>
            <CIcon icon="home" size="18" />
          </template>
        </CButton>
        <CButton
          v-if="showEditButton"
          v-tooltip="t('canvasUI.canvasBuilder.components.edit')"
          class="control-bar__button h-11 w-11"
          :class="{ 'mt-3': !showHomeButton }"
          secondary
          strong
          @click="isEditMode = true"
        >
          <template #icon>
            <CIcon icon="pencil" size="18" />
          </template>
        </CButton>
        <template v-if="shouldShowLockButton">
          <CButton
            v-if="canvasLock"
            v-tooltip="canvasLockTooltip"
            class="control-bar__button h-11 w-11"
            :class="{ 'mt-3': !showEditButton && !showHomeButton }"
            :disabled="!isCurrentUserLocker && !isOrgAdmin"
            :loading="isChangingLock"
            strong
            type="error"
            @click="onUnlockClick"
          >
            <template #icon>
              <CIcon color="success" icon="unlock" size="18" />
            </template>
          </CButton>
          <CButton
            v-else
            v-tooltip="t('canvasUI.canvasBuilder.components.lock')"
            class="control-bar__button h-11 w-11"
            :loading="isChangingLock"
            secondary
            strong
            @click="onLockClick"
          >
            <template #icon>
              <CIcon icon="lock" size="18" />
            </template>
          </CButton>
        </template>
        <CButton
          v-if="showFullscreenButton"
          v-tooltip="t('canvasUI.canvasBuilder.components.fullscreen')"
          class="control-bar__button h-11 w-11"
          :class="{ 'mt-3': !showEditButton && !showHomeButton }"
          secondary
          strong
          @click="() => enterFullscreen()"
        >
          <template #icon>
            <CIcon icon="expand" size="18" />
          </template>
        </CButton>
        <CButton
          v-if="showNotesButton"
          v-tooltip="t('canvasUI.canvasBuilder.components.notes')"
          class="control-bar__button h-11 w-11"
          secondary
          strong
          @click="appEmit('toggleNotes')"
        >
          <template #icon>
            <CIcon icon="note-sticky" size="18" />
          </template>
        </CButton>
        <CButton
          v-if="showExitFullscreenButton"
          v-tooltip="t('canvasUI.canvasBuilder.components.fullscreenExit')"
          class="control-bar__button h-11 w-11"
          secondary
          strong
          @click="exitFullscreen"
        >
          <template #icon>
            <CIcon icon="compress" size="18" />
          </template>
        </CButton>
        <CButton
          v-if="showEditButton && showSharingButton"
          v-tooltip="t('canvasUI.canvasBuilder.components.share')"
          class="control-bar__button h-11 w-11"
          :disabled="downloadInProgress"
          :loading="sharingInProgress"
          secondary
          strong
          @click="onShareClick"
        >
          <template #icon>
            <CIcon icon="link" size="18" />
          </template>
        </CButton>
      </template>
    </template>

    <template #middle>
      <template v-if="isEditMode">
        <!-- Add button component> -->
        <CButton
          v-tooltip="t('canvasUI.canvasBuilder.components.add', { canvasName: activeCanvas?.name })"
          class="control-bar__button h-11 w-11"
          :disabled="isAddButtonDisabled"
          secondary
          strong
          @click="onAddNew"
        >
          <template #icon>
            <CIcon icon="plus" size="18" />
          </template>
        </CButton>

        <!-- Theme button -->
        <CButton
          v-tooltip="t('canvasUI.canvasBuilder.components.theme')"
          class="control-bar__button h-11 w-11"
          :disabled="isViewOnlyMode"
          secondary
          strong
          @click="onThemeEdit"
        >
          <template #icon>
            <CIcon icon="palette" size="18" />
          </template>
        </CButton>

        <!-- AI button -->
        <CButton
          v-if="launchDarkly.enable_pia_assistant"
          v-tooltip="t('canvasUI.canvasBuilder.components.pia')"
          class="control-bar__button h-11 w-11"
          :disabled="isViewOnlyMode"
          secondary
          strong
          @click="onAiClick"
        >
          <template #icon>
            <CIcon icon="cat-space" size="18" />
          </template>
        </CButton>
      </template>

      <InstalledEmbeddableShortcuts />
      <!-- Apps button with conditional adaptive visibility -->
      <AdaptivePopupAppsButtons
        v-if="launchDarkly.enable_adaptive_popup_apps"
        button-class="control-bar__button h-11 w-11"
        placement="left"
      />
      <PopupAppsButtons v-else button-class="control-bar__button h-11 w-11" placement="left" />
    </template>

    <template v-if="isEditMode" #bottom>
      <CButton
        v-tooltip="t('canvasUI.canvasBuilder.components.presentation')"
        class="control-bar__button h-11 w-11"
        strong
        type="primary"
        @click="handleToggleEditMode"
      >
        <template #icon>
          <CIcon icon="presentation-screen" size="18" />
        </template>
      </CButton>

      <ControlBarReorder v-tooltip="t('canvasUI.canvasBuilder.components.reorder')" :disabled="isViewOnlyMode" />

      <CButton
        v-tooltip="t('canvasUI.canvasBuilder.components.save')"
        class="control-bar__button h-11 w-11"
        :disabled="isSameAsSaved"
        :loading="isSavingCanvas"
        secondary
        strong
        @click="onSaveClick"
      >
        <template #icon>
          <CIcon icon="floppy-disk" size="18" />
        </template>
      </CButton>
    </template>

    <template v-else-if="showNextButton && !isLastPage" #bottom>
      <CButton
        v-tooltip="t('canvasUI.canvasBuilder.components.nextPage')"
        class="h-14 w-11"
        secondary
        size="large"
        strong
        @click="goToNextPage"
      >
        <template #icon>
          <CIcon icon="right" size="18" />
        </template>
      </CButton>
    </template>
  </NavigationWrapper>

  <div
    v-if="isContentVisible && isEditMode"
    class="control-bar__reorder-drawer w-[300px] flex flex-grow-0 flex-shrink-0 flex-col gap-2 px-2 pb-4 pt-4 overflow-auto"
    :class="{
      'pt-6': !isEditMode,
      'pt-3': isEditMode,
    }"
  >
    <ReorderTitle />
    <ReorderContent />
  </div>

  <AiPromptModal
    v-if="isShowingAiPrompt"
    :isProcessing="isProcessing"
    @close="isShowingAiPrompt = false"
    @submit="handleAiPromptSubmit"
  />
</template>

<script setup lang="ts">
import { merge } from 'lodash-es'
import { computed, type ComputedRef, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useOnline } from '@vueuse/core'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useToast from '@lib/composables/useToast'
import ControlBarReorder from './ControlBarReorder.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import { CanvasBuilderMode, ComponentTypes } from '@canvas-builder/types/canvas'
import useAiPrompt from '@canvas-builder/composables/useAiPrompt'
import useAppEmit from '@lib/composables/appEmit.use'
import AiPromptModal from '@canvas-builder/components/container/AiPrompt/AiPromptModal.vue'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import useShareCanvas from '@lib/components/savedCanvases/CSavedCanvasesManagementShareCanvas.use'
import NavigationWrapper from '@canvas-builder/components/container/NavigationWrapper/NavigationWrapper.vue'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import ReorderTitle from '@canvas-builder/components/container/ControlBar/ReorderTitle.vue'
import ReorderContent from '@canvas-builder/components/container/ControlBar/ReorderContent.vue'
import useReorderComponent from '@canvas-builder/composables/useReorderComponent'
import useThemeVars from '@lib/composables/useThemeVars'
import tooltipDirective from '@lib/directives/tooltip'
import { scrollCanvasToTop } from '@canvas-builder/util/canvas.util'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import InstalledEmbeddableShortcuts from '@canvas-builder/components/container/ControlBar/InstalledEmbeddableShortcuts.vue'
import { type User } from '@openapi'
import useCanvasLocks from '@lib/composables/useCanvasLocks'
import useCanvasHistory from '@canvas-builder/composables/useCanvasHistory'
import { CanvasHistoryAction } from '@canvas-builder/types/canvas'
import AdaptivePopupAppsButtons from '@canvas-builder/components/container/PopupApps/AdaptivePopupAppsButtons.vue'
import PopupAppsButtons from '@canvas-builder/components/container/PopupApps/PopupAppsButtons.vue'
import useCanvasCompletionWizard from '@canvas-builder/composables/useCanvasCompletionWizard'
import useControlBarCollapse from '@canvas-builder/composables/useControlBarCollapse'

const props = defineProps<{
  hideEdit?: boolean
}>()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const myUser = inject<ComputedRef<User | null>>(
  'myUser',
  computed(() => null),
)

const { t } = useI18n()
const { appEmit } = useAppEmit()
const isSavingCanvas = ref(false)
const isShowingAiPrompt = ref(false)
const online = useOnline()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective
const { isProcessing, processAiPrompt } = useAiPrompt()
const { toast } = useToast()
const {
  activeCanvas,
  componentSelectedMode,
  isAdmin,
  isImpact,
  isCanvas,
  isEditMode,
  isSameAsSaved,
  isViewOnlyMode,
  mode,
  myUserId,
  onDownloadCanvas,
  onTrackShare,
  selectedComponentType,
  share,
  isBlock,
  isTemplate,
  isCanvasTemplate,
  isSection,
  enterFullscreen,
  exitFullscreen,
  isFullscreen,
  saveCanvasContent,
  saveCanvasContext,
  setAddMode,
  setComponentEditMode,
  setComponentSelectedMode,
  navigateToHome,
  selectedComponentParent,
  selectedComponentParentType,
} = useCanvas()
const { isEditingCanvasTheme } = useCanvasTheme()
const { copySharingUrlToClipboard, emitShareEvent } = useShareCanvas(share.value!, {
  useShortUrl: !!launchDarkly.value.use_short_url_for_shared_link,
})
const { getDefaultCrmShape } = useCrmShape()
const { goToNextPage, isLastPage } = useCanvasPages()
const { isContentVisible } = useReorderComponent()
const { areControlBarsCollapsed } = useControlBarCollapse()
const downloadInProgress = ref(false)
const sharingInProgress = ref(false)
const selectedFileFormat = ref<'pdf' | 'pptx'>('pdf')

const { addCanvasHistoryItem } = useCanvasHistory()

const showNextButton = computed(
  () => (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles' && !isEditMode.value,
)
const showHomeButton = computed(() => isCanvas.value || isCanvasTemplate.value)
const showSharingButton = computed(
  () =>
    isCanvas.value &&
    mode.value === CanvasBuilderMode.IMPACT &&
    !pitcherSettings.value.canvas?.disable_shared_link_creation,
)
const showDownloadButton = computed(
  () => isCanvas.value && mode.value === CanvasBuilderMode.IMPACT && !launchDarkly.value.disable_download_canvas,
)
const showFullscreenButton = computed(
  () =>
    !launchDarkly.value.disable_fullscreen_canvas_builder &&
    (!pitcherSettings.value.alternative_canvas_fullscreen || !isFullscreen.value),
)
const showNotesButton = computed(
  () => launchDarkly.value.allow_note_taking && mode.value === CanvasBuilderMode.IMPACT && isFullscreen.value,
)
const showWizardButton = computed(() => isImpact.value && isCanvas.value && isCompletionWizardEnabled.value)

const {
  lockCanvas,
  unlockCanvas,
  getCanvasLock,
  isLockedByCurrentUser,
  isChangingLock,
  getCanvasLockTooltip,
  isOrgAdmin,
} = useCanvasLocks()
const { isCompletionWizardEnabled } = useCanvasCompletionWizard()

const canvasLock = computed(() => (activeCanvas.value?.id ? getCanvasLock(activeCanvas.value.id) : null))
const isCurrentUserLocker = computed(() =>
  activeCanvas.value?.id ? isLockedByCurrentUser(activeCanvas.value.id) : false,
)
const isLockedBySomeoneElse = computed(() => !!canvasLock.value && !isCurrentUserLocker.value)
const canvasLockTooltip = computed(() => {
  if (!activeCanvas.value?.id) return ''

  if (isCurrentUserLocker.value) {
    return t('canvasUI.canvasBuilder.components.unlock')
  }

  return getCanvasLockTooltip(activeCanvas.value.id)
})
const shouldShowLockButton = computed(() => {
  return (
    !!launchDarkly.value.enable_canvas_locks &&
    ((isAdmin.value && isCanvasTemplate.value) || (isImpact.value && isCanvas.value))
  )
})

function handleToggleEditMode() {
  isEditMode.value = !isEditMode.value
  if (!isEditMode.value) scrollCanvasToTop()
}

function onSettingsClick() {
  setComponentEditMode(ComponentTypes.Root)
}

function onThemeEdit() {
  isEditingCanvasTheme.value = !isEditingCanvasTheme.value
}

function onAiClick() {
  isShowingAiPrompt.value = !isShowingAiPrompt.value
}

async function handleAiPromptSubmit(prompt: string) {
  try {
    // Process the prompt - AI changes will be applied automatically
    // Updates and saves the canvas in one operation
    await processAiPrompt(prompt)

    // Show success message and close the modal
    toast.success(t('canvasUI.canvasBuilder.notifications.aiPrompt.success'))

    // Close the modal when processing is complete and successful
    isShowingAiPrompt.value = false
  } catch (error) {
    console.error('Error processing AI prompt:', error)
    toast.error(t('canvasUI.canvasBuilder.notifications.aiPrompt.error'))

    // Keep the modal open when an error occurs so the user can try again
    // The error toast will inform them that something went wrong
  }
}

async function onLockClick() {
  if (!activeCanvas.value?.id) {
    throw new Error('Canvas id is not defined')
  }

  try {
    await lockCanvas(activeCanvas.value.id)
  } catch (err) {
    console.error('Error locking canvas:', err)
  }
}

async function onUnlockClick() {
  if (!canvasLock.value?.id) {
    throw new Error('Canvas lock id is not defined')
  }

  try {
    await unlockCanvas(canvasLock.value.id)
  } catch (err) {
    console.error('Error unlocking canvas:', err)
  }
}

async function onShareClick() {
  if (!activeCanvas.value?.id) {
    throw new Error('Canvas id is not defined')
  }

  try {
    sharingInProgress.value = true
    await saveCanvasContext(
      merge({}, activeCanvas.value?.context ?? {}, getDefaultCrmShape({ myUser: myUser.value })),
      { showToast: false },
    )
    if (launchDarkly.value.use_advanced_sharing_modal) {
      emitShareEvent(activeCanvas.value)
    } else {
      await copySharingUrlToClipboard(activeCanvas.value)
      toast.success(t('canvasUI.canvasBuilder.notifications.share.success'))
    }
  } catch (e) {
    toast.error(t('canvasUI.canvasBuilder.notifications.share.error'))
  } finally {
    onTrackShare.value?.(activeCanvas.value.id)
    sharingInProgress.value = false
  }
}

async function onDownloadCanvasClick() {
  // Legacy download behavior only
  if (!onDownloadCanvas.value) {
    throw new Error('Download canvas function is not defined')
  }

  downloadInProgress.value = true

  try {
    await onDownloadCanvas.value(activeCanvas.value!, selectedFileFormat.value)

    if (activeCanvas.value?.metadata?.pitcher_custom_download_url) {
      return
    }

    toast.success(t('canvasUI.canvasBuilder.notifications.download.success'))
  } catch (e) {
    toast.error(t('canvasUI.canvasBuilder.notifications.download.error'))
  } finally {
    downloadInProgress.value = false
  }
}

async function onSaveClick() {
  if (!activeCanvas.value?.id) return
  try {
    isSavingCanvas.value = true
    await saveCanvasContent()

    addCanvasHistoryItem({
      action: CanvasHistoryAction.SAVE,
      componentType: ComponentTypes.Root,
    })
  } catch (error) {
    console.error(error)
  } finally {
    isSavingCanvas.value = false
  }
}

function onWizardClick() {
  if (window.retriggerWizard) {
    window.retriggerWizard()
  }
}

function onAddNew() {
  if (!componentSelectedMode.value) {
    setAddMode(ComponentTypes.Root)
    setComponentSelectedMode() // clear selected component
    return
  }

  const parent = selectedComponentParent.value

  if (!parent) {
    setAddMode(ComponentTypes.Root, null, componentSelectedMode.value.id)
  } else if (parent.type === ComponentTypes.Grid) {
    // TODO: decide what to do on grid item addition from control bar
  } else {
    setAddMode(parent.type, parent.id, componentSelectedMode.value.id)
  }
}

const isAddButtonDisabled = computed(() => {
  return (
    selectedComponentType.value === ComponentTypes.GridItem ||
    (componentSelectedMode.value &&
      selectedComponentParentType.value === ComponentTypes.Scribble &&
      (selectedComponentType.value === ComponentTypes.Text ||
        selectedComponentType.value === ComponentTypes.Multimedia ||
        selectedComponentType.value === ComponentTypes.DynamicDataTable ||
        selectedComponentType.value === ComponentTypes.Html ||
        selectedComponentType.value === ComponentTypes.Embeddable)) ||
    isViewOnlyMode.value
  )
})

const showExitFullscreenButton = computed(
  () => pitcherSettings.value.alternative_canvas_fullscreen && isFullscreen.value,
)

const showEditButton = computed(() => {
  if (props.hideEdit || mode.value === CanvasBuilderMode.SHARING || isLockedBySomeoneElse.value) {
    return false
  }

  // Hide edit button when in fullscreen mode with alternative_canvas_fullscreen enabled
  if (showExitFullscreenButton.value) {
    return false
  }
  const isImpact = mode.value === 'impact'

  if (isImpact && isBlock.value) return false

  const isTemplateOrSectionCreator = isImpact && activeCanvas.value?.created_by?.id === myUserId.value
  const isCanvas = isImpact && !isTemplate.value && !isSection.value && !isBlock.value
  const isTemplateAndImpactEditAllowed =
    isImpact &&
    isTemplate.value &&
    !isSection.value &&
    !isBlock.value &&
    launchDarkly.value.enable_canvas_template_edit_in_impact

  return isAdmin.value || isTemplateOrSectionCreator || isCanvas || isTemplateAndImpactEditAllowed
})

const isCollapseFeatureEnabled = computed(() => !!launchDarkly.value.enable_canvas_control_bars_collapse)

watch(
  canvasLock,
  (lock) => {
    if (lock && !isCurrentUserLocker.value && isEditMode.value) {
      console.info('Locking edit mode since it was locked by another user...', lock)
      isEditMode.value = false
    }
  },
  { immediate: true, deep: true },
)
</script>

<style lang="scss" scoped>
.control-bar__reorder-drawer {
  background: v-bind('themeVars.primary6');
}
</style>
